import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import { cn } from '../../lib/utils';

/**
 * Nava Button — matches the Figma Button / Icon Button spec.
 * Axes: variant (6) × size (xs–xl) × state (default/hover/focus/disabled/loading).
 * Painted with Nava token utility classes; spacing via the `space-*` scale.
 */
const buttonVariants = cva(
  'h-8 relative inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus-ring border disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-brand hover:bg-brand-hover text-foreground border-border-brand',
        default: 'bg-surface hover:bg-hover text-foreground border-border',
        outline: 'bg-transparent hover:bg-hover text-foreground border-border',
        ghost: 'bg-transparent hover:bg-hover text-foreground border-transparent',
        destructive:
          'bg-danger-subtle hover:bg-danger-subtle text-foreground border-border-danger [&_svg]:text-icon-danger',
        link: 'bg-transparent text-foreground border-transparent underline underline-offset-4 hover:opacity-80',
      },
      // Height is emergent: line-height + vertical padding = the Figma frame height
      // (xs 26, sm 28, md 32, lg 36, xl 40). No explicit h-* — scales with font-size.
      size: {
        xs: 'px-space-8 py-space-4 gap-space-4 rounded-md type-text-xs [&_svg]:size-icon-sm',
        sm: 'px-space-8 py-space-4 gap-space-4 rounded-md type-text-sm [&_svg]:size-icon-sm',
        md: 'px-space-12 py-space-6 gap-space-4 rounded-md type-text-sm [&_svg]:size-icon-md',
        lg: 'px-space-16 py-space-8 gap-space-4 rounded-10 type-text-sm [&_svg]:size-icon-lg',
        xl: 'px-space-16 py-space-8 gap-space-4 rounded-10 type-text-md [&_svg]:size-icon-xl',
      },
      // Icon-only: drop horizontal padding and square off the emergent height
      // (vertical padding still drives the height, aspect-square matches width to it).
      iconOnly: {
        true: 'aspect-square px-0',
        false: '',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        className: 'h-auto border-0 p-0 hover:bg-transparent',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      iconOnly: false,
      block: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<ButtonVariantProps, 'iconOnly' | 'block'> {
  ref?: React.Ref<HTMLButtonElement>;
  /** Show a spinner and disable the button. */
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** Square, label-less icon button (the Figma Icon Button component). */
  iconOnly?: boolean;
  /** Stretch to the full width of the container. */
  block?: boolean;
  /** Render as the child element (Radix Slot) — e.g. wrap an `<a>`. */
  asChild?: boolean;
}

function renderLabel(children: React.ReactNode) {
  if (typeof children === 'string') {
    return <span className="truncate">{children}</span>;
  }
  return children;
}

/** Button following the Nava design system — 6 variants, 5 sizes, icon-only + loading support. */
export function Button({
  ref,
  variant = 'primary',
  size = 'md',
  type = 'button',
  loading = false,
  iconLeft,
  iconRight,
  iconOnly = false,
  block = false,
  asChild = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const leftIcon = loading ? <Loader2 className="animate-spin" /> : iconLeft;

  const sharedClassName = cn(buttonVariants({ variant, size, iconOnly, block }), className);

  // asChild: Slot needs exactly one child and merges our props onto it.
  // The consumer owns the element and its content — we only forward styling.
  if (asChild) {
    return (
      <Slot.Slot
        ref={ref}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        className={sharedClassName}
        {...rest}
      >
        {children}
      </Slot.Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={sharedClassName}
      {...rest}
    >
      {leftIcon}
      {!iconOnly && renderLabel(children)}
      {iconOnly && !loading && children}
      {iconRight}
    </button>
  );
}

export { buttonVariants };
